import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import { get } from "@/lib/api";
import { useUrlFilters } from "@/lib/useUrlFilters";
import { Filters, filtersToParams } from "@/components/Filters";
import { STATUS_HEX } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";

export default function MapView() {
  const { f, set, toggleList, reset } = useUrlFilters();
  const { open } = useEventDrawer();
  const params = filtersToParams(f);
  const { data } = useQuery({ queryKey: ["events", "map", params], queryFn: () => get("/events", { ...params, has_coords: 1, page_size: 2000 }) });
  const { data: communes } = useQuery({ queryKey: ["communes-mon"], queryFn: () => get("/communes", { monitored: true }) });
  const items = data?.items || [];
  const pts = items.length ? items : (communes || []).filter((c) => c.lat);
  const center = pts.length ? [pts.reduce((a, p) => a + p.lat, 0) / pts.length, pts.reduce((a, p) => a + p.lon, 0) / pts.length] : [46.6, 2.4];

  const [filtersOpen, setFiltersOpen] = useState(false);
  return (
    <div className="flex h-[calc(100dvh-var(--chrome-h))]">
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="left" aria-describedby={undefined} className="w-[88vw] max-w-sm p-4 overflow-y-auto safe-top safe-bottom">
          <SheetTitle className="font-display text-base mb-2">Filtres</SheetTitle>
          <Filters f={f} set={set} toggleList={toggleList} reset={reset} />
          <Button className="w-full mt-4" size="sm" onClick={() => setFiltersOpen(false)}>Afficher la carte</Button>
        </SheetContent>
      </Sheet>
      <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 dark:border-slate-800 overflow-y-auto p-4 bg-white dark:bg-slate-900">
        <Filters f={f} set={set} toggleList={toggleList} reset={reset} />
      </aside>
      <div className="flex-1 relative isolate" data-testid="map-container">
        <Button data-testid="map-mobile-filters-btn" size="sm" variant="outline" className="lg:hidden absolute bottom-4 fab-bottom left-3 z-[500] h-8 text-xs bg-white dark:bg-slate-900 shadow" onClick={() => setFiltersOpen(true)}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Filtres</Button>
        <div className="absolute top-3 right-3 z-[500] panel px-3 py-2 text-[11px] space-y-1 max-w-[60vw]">
          <div className="font-mono" data-testid="map-count">{items.length} événement(s) géolocalisé(s)</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full border-2 border-indigo-600" />Signal faible</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_HEX.NOUVEAU }} />Nouveau</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_HEX.MODIFIÉ }} />Modifié</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_HEX.ANNULÉ }} />Annulé</div>
          <div className="text-slate-400">Position au centre de la commune si l'adresse est inconnue</div>
        </div>
        <MapContainer key={center.join(",")} center={center} zoom={items.length || communes?.length ? 11 : 6} className="h-full w-full">
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {(communes || []).filter((c) => c.lat).map((c) => (
            <CircleMarker key={c.insee} center={[c.lat, c.lon]} radius={4} pathOptions={{ color: "#0f172a", weight: 1, fillOpacity: 0.15 }}>
              <Tooltip>{c.name} ({c.insee})</Tooltip>
            </CircleMarker>
          ))}
          {items.map((e) => (
            <CircleMarker key={e.id} center={[e.lat, e.lon]} radius={e.weak_signal ? 9 : 6} eventHandlers={{ click: () => open(e.id) }}
              pathOptions={{ color: e.weak_signal ? "#4f46e5" : "#fff", weight: e.weak_signal ? 3 : 1, fillColor: STATUS_HEX[e.status] || "#475569", fillOpacity: 0.85 }}>
              <Tooltip><b>{e.title}</b><br />{e.next_date} {e.start_time || ""} · {e.commune_name}</Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
